import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Trash2, X, PackageCheck, Printer } from "lucide-react";
import { api, errMsg, API } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtIDR, fmtNum, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function ItemsEditor({ items, setItems, ingredients, units, testPrefix }) {
  const { t } = useLang();
  const unitAbbr = (ingId) => {
    const ing = ingredients.find((i) => i.id === ingId);
    const u = (units || []).find((x) => x.id === ing?.unit_id);
    return u?.abbreviation || "";
  };
  const update = (idx, patch) => { const arr = [...items]; arr[idx] = { ...arr[idx], ...patch }; setItems(arr); };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_110px_130px_1fr_32px] gap-2 text-[10px] uppercase tracking-wider text-muted-foreground px-0.5">
        <span>{t("name")}</span><span>{t("purchaseQty")}</span><span>{t("purchasePrice")}</span><span>{t("pricePerUnit")}</span><span />
      </div>
      {items.map((it, idx) => {
        const unit = unitAbbr(it.ingredient_id);
        const perUnit = it.qty > 0 ? (it.pack_price || 0) / it.qty : 0;
        return (
          <div key={it.ingredient_id || `empty-${idx}`} className="grid grid-cols-[1fr_110px_130px_1fr_32px] gap-2 items-center">
            <Select value={it.ingredient_id} onValueChange={(v) => update(idx, { ingredient_id: v })}>
              <SelectTrigger data-testid={`${testPrefix}-item-select-${idx}`}><SelectValue placeholder="..." /></SelectTrigger>
              <SelectContent>{ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Input data-testid={`${testPrefix}-item-qty-${idx}`} type="number" value={it.qty} onChange={(e) => update(idx, { qty: Number(e.target.value) })} />
              <span className="text-xs text-muted-foreground shrink-0 w-9" data-testid={`${testPrefix}-item-unit-${idx}`}>{unit || "-"}</span>
            </div>
            <Input data-testid={`${testPrefix}-item-price-${idx}`} type="number" value={it.pack_price} onChange={(e) => update(idx, { pack_price: Number(e.target.value) })} />
            <div className="text-xs font-mono text-muted-foreground" data-testid={`${testPrefix}-item-perunit-${idx}`}>
              {fmtIDR(perUnit)}/{unit || "satuan"} · <span className="text-foreground font-semibold">{fmtIDR((it.qty || 0) * perUnit)}</span>
            </div>
            <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="p-1.5 text-muted-foreground hover:text-rose-600"><X className="w-4 h-4" /></button>
          </div>
        );
      })}
      <Button size="sm" variant="outline" data-testid={`${testPrefix}-add-item-btn`} onClick={() => setItems([...items, { ingredient_id: "", qty: 1, pack_price: 0 }])}><Plus className="w-3.5 h-3.5 mr-1" />Item</Button>
    </div>
  );
}

export default function Purchasing() {
  const { t } = useLang();
  const { outlet } = useOutletContext() || {};
  const [pos, setPos] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [units, setUnits] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [poOpen, setPoOpen] = useState(false);
  const [purOpen, setPurOpen] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [poForm, setPoForm] = useState({ supplier_id: "", tax_pct: 0, expected_date: "", notes: "", items: [] });
  const [purForm, setPurForm] = useState({ supplier_id: "", invoice_number: "", tax_pct: 0, payment_method: "cash", items: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = outlet ? { outlet_id: outlet } : {};
      const [po, pu, m, ig] = await Promise.all([
        api.get("/purchase-orders", { params }), api.get("/purchases", { params }), api.get("/meta/all"), api.get("/ingredients"),
      ]);
      setPos(po.data);
      setPurchases(pu.data);
      setSuppliers(m.data.suppliers);
      setUnits(m.data.units || []);
      setOutlets(m.data.outlets || []);
      setIngredients(ig.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [outlet]);

  useEffect(() => { load(); }, [load]);

  const openPoForm = (po) => {
    setEditingPo(po || null);
    setPoForm(po ? { supplier_id: po.supplier_id, outlet_id: po.outlet_id || "", tax_pct: po.tax || 0, expected_date: po.expected_date || "", notes: po.notes || "", items: po.items.map((i) => ({ ingredient_id: i.ingredient_id, qty: i.qty, pack_price: Math.round((i.qty || 0) * (i.price || 0) * 100) / 100 })) } : { supplier_id: "", outlet_id: outlet || "", tax_pct: 0, expected_date: "", notes: "", items: [] });
    setPoOpen(true);
  };

  const toPayloadItems = (items) => items
    .filter((i) => i.ingredient_id)
    .map((i) => ({ ingredient_id: i.ingredient_id, qty: Number(i.qty) || 0, price: i.qty > 0 ? Math.round(((i.pack_price || 0) / i.qty) * 10000) / 10000 : 0 }));

  const savePo = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...poForm, outlet_id: poForm.outlet_id || outlet || "", items: toPayloadItems(poForm.items) };
      if (editingPo) await api.put(`/purchase-orders/${editingPo.id}`, payload);
      else await api.post("/purchase-orders", payload);
      toast.success("OK");
      setPoOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const receivePo = async (po) => {
    try {
      await api.post(`/purchase-orders/${po.id}/receive`);
      toast.success(`${po.po_number} received — stok & HPP diperbarui`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deletePo = async (po) => {
    if (!window.confirm(po.status === "received" ? t("confirmDeletePurchase") : t("confirmDelete"))) return;
    try { await api.delete(`/purchase-orders/${po.id}`); toast.success("OK"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const deletePurchase = async (p) => {
    if (!window.confirm(t("confirmDeletePurchase"))) return;
    try { await api.delete(`/purchases/${p.id}`); toast.success("OK"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const savePurchase = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/purchases", { ...purForm, outlet_id: purForm.outlet_id || outlet || "", items: toPayloadItems(purForm.items) });
      toast.success("OK — stok & HPP diperbarui");
      setPurOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const statusBadge = (s) => {
    const map = {
      draft: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700",
      received: "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800",
    };
    return <span className={`border px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[s] || map.draft}`}>{s}</span>;
  };

  return (
    <div data-testid="purchasing-page">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex-1">{t("nav_purchasing")}</h1>
        <Button variant="outline" data-testid="purchase-add-btn" onClick={() => { setPurForm({ supplier_id: "", outlet_id: outlet || "", invoice_number: "", tax_pct: 0, payment_method: "cash", items: [] }); setPurOpen(true); }}><Plus className="w-4 h-4 mr-1.5" />{t("purchases")}</Button>
        <Button data-testid="po-add-btn" onClick={() => openPoForm(null)} className="bg-amber-600 hover:bg-amber-700 text-white"><Plus className="w-4 h-4 mr-1.5" />{t("createPO")}</Button>
      </div>

      <Tabs defaultValue="po">
        <TabsList data-testid="purchasing-tabs">
          <TabsTrigger value="po" data-testid="tab-po">{t("purchaseOrders")}</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases">{t("purchases")}</TabsTrigger>
        </TabsList>
        <TabsContent value="po">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs mt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/50">
                  {["PO No", t("supplier"), t("outlet"), "Items", t("total"), t("status"), t("date"), t("actions")].map((h) => <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
                </tr></thead>
                <tbody>
                  {pos.map((po) => (
                    <tr key={po.id} data-testid={`po-row-${po.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono font-semibold">{po.po_number}</td>
                      <td className="px-4 py-3">{po.supplier_name}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{po.outlet_name || "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{po.items.length} item</td>
                      <td className="px-4 py-3 font-mono">{fmtIDR(po.total)}</td>
                      <td className="px-4 py-3">{statusBadge(po.status)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(po.created_at)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <a data-testid={`po-print-${po.id}`} href={`${API}/purchase-orders/${po.id}/print`} target="_blank" rel="noreferrer" title="Cetak PO" className="inline-flex p-1.5 text-muted-foreground hover:text-foreground align-middle mr-1"><Printer className="w-4 h-4" /></a>
                        {po.status === "draft" && (
                          <>
                            <Button size="sm" variant="outline" data-testid={`po-receive-${po.id}`} onClick={() => receivePo(po)} className="mr-1.5"><PackageCheck className="w-3.5 h-3.5 mr-1" />{t("receive")}</Button>
                            <Button size="sm" variant="outline" data-testid={`po-edit-${po.id}`} onClick={() => openPoForm(po)} className="mr-1.5">{t("edit")}</Button>
                          </>
                        )}
                        <button data-testid={`po-delete-${po.id}`} onClick={() => deletePo(po)} className="p-1.5 text-muted-foreground hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {pos.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="purchases">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs mt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/50">
                  {["PUR No", t("supplier"), t("outlet"), "Invoice", "Items", t("total"), t("date"), t("actions")].map((h) => <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
                </tr></thead>
                <tbody>
                  {purchases.map((p) => (
                    <tr key={p.id} data-testid={`purchase-row-${p.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono font-semibold">{p.purchase_number}</td>
                      <td className="px-4 py-3">{p.supplier_name}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{p.outlet_name || "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{p.invoice_number || "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{p.items.length} item</td>
                      <td className="px-4 py-3 font-mono">{fmtIDR(p.total)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(p.created_at)}</td>
                      <td className="px-4 py-3">
                        <button data-testid={`purchase-delete-${p.id}`} onClick={() => deletePurchase(p)} className="p-1.5 text-muted-foreground hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {purchases.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="po-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingPo ? t("edit") : t("createPO")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("supplier")}</Label>
                <Select value={poForm.supplier_id} onValueChange={(v) => setPoForm({ ...poForm, supplier_id: v })}>
                  <SelectTrigger data-testid="po-supplier-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("outlet")}</Label>
                <Select value={poForm.outlet_id} onValueChange={(v) => setPoForm({ ...poForm, outlet_id: v })}>
                  <SelectTrigger data-testid="po-outlet-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                  <SelectContent>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("tax")} (0-1)</Label>
                <Input data-testid="po-tax-input" type="number" step="0.01" value={poForm.tax_pct} onChange={(e) => setPoForm({ ...poForm, tax_pct: Number(e.target.value) })} className="mt-1.5" /></div>
            </div>
            <ItemsEditor items={poForm.items} setItems={(items) => setPoForm({ ...poForm, items })} ingredients={ingredients} units={units} testPrefix="po" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPoOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="po-save-btn" onClick={savePo} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={purOpen} onOpenChange={setPurOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="purchase-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("add")} {t("purchases")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("supplier")}</Label>
                <Select value={purForm.supplier_id} onValueChange={(v) => setPurForm({ ...purForm, supplier_id: v })}>
                  <SelectTrigger data-testid="purchase-supplier-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("outlet")}</Label>
                <Select value={purForm.outlet_id} onValueChange={(v) => setPurForm({ ...purForm, outlet_id: v })}>
                  <SelectTrigger data-testid="purchase-outlet-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                  <SelectContent>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Invoice No</Label>
                <Input data-testid="purchase-invoice-input" value={purForm.invoice_number} onChange={(e) => setPurForm({ ...purForm, invoice_number: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("tax")} (0-1)</Label>
                <Input data-testid="purchase-tax-input" type="number" step="0.01" value={purForm.tax_pct} onChange={(e) => setPurForm({ ...purForm, tax_pct: Number(e.target.value) })} className="mt-1.5" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("paymentMethod")}</Label>
                <Select value={purForm.payment_method} onValueChange={(v) => setPurForm({ ...purForm, payment_method: v })}>
                  <SelectTrigger data-testid="purchase-payment-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t("cash")}</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <ItemsEditor items={purForm.items} setItems={(items) => setPurForm({ ...purForm, items })} ingredients={ingredients} units={units} testPrefix="pur" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPurOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="purchase-save-btn" onClick={savePurchase} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
